import { Box, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Divider from 'components/divider';
import { Summary } from 'views/bluviu-nutrition/features/scenarios/types/scenario-result.type';
import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import NewList from 'components/new-list';

type FieldFormat = 'plain' | 'gram' | 'kilogram' | 'percent';

type FeedProgramSummary = {
  labelKey: string;
  summaryKey: keyof Summary;
  format: FieldFormat;
  maximumFractionDigits?: number;
};

const ROW_GROUPS: FeedProgramSummary[][] = [
  [
    { labelKey: 'daysToReach', summaryKey: 'daysToReach', format: 'plain', maximumFractionDigits: 0 },
    { labelKey: 'weightGainGrams', summaryKey: 'weightGainGrams', format: 'gram', maximumFractionDigits: 2 },
    { labelKey: 'feedIntakeGrams', summaryKey: 'feedIntakeGrams', format: 'gram', maximumFractionDigits: 2 },
    { labelKey: 'carcassYieldPercent', summaryKey: 'carcassYieldPercent', format: 'percent' },
    { labelKey: 'filletFatPercent', summaryKey: 'filletFatPercent', format: 'percent' },
  ],
  [
    { labelKey: 'feedCostOfGrowth', summaryKey: 'averageFeedCost', format: 'plain', maximumFractionDigits: 2 },
    { labelKey: 'hogGainGrams', summaryKey: 'hogGainGrams', format: 'gram', maximumFractionDigits: 2 },
    {
      labelKey: 'globalWarmingPotentialCo2KgPerKgFeed',
      summaryKey: 'globalWarmingPotentialCo2KgPerKgFeed',
      format: 'kilogram',
      maximumFractionDigits: 2,
    },
  ],
];

const HeaderTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: 12,
  color: theme.palette.darkBlue.main,
}));

const renderValue = (field: FeedProgramSummary, summary: Summary) => {
  const value = Number(summary[field.summaryKey]);
  const unitProps = field.format !== 'plain' ? { style: 'unit' as const, unit: field.format } : {};
  return <FormattedNumber value={value} maximumFractionDigits={field.maximumFractionDigits} {...unitProps} />;
};

type Props = {
  summary: Summary;
};

const ScenarioResultRowBody: React.FC<Props> = ({ summary }) => {
  const PAGE_NAME_ID = 'bluViuNutrition.viewScenarioResultGt2';

  return (
    <>
      <Divider dark />
      <Box display={'flex'} flexDirection={'column'} py={4}>
        <NewList size="medium" aria-label="Feed Program Summary">
          {ROW_GROUPS.map((fields, groupIndex) => (
            <React.Fragment key={groupIndex}>
              <TableHead>
                <TableRow>
                  {fields.map((field) => (
                    <TableCell key={field.labelKey} align="left" sx={{ p: '8px 16px' }}>
                      <HeaderTypography>
                        <FormattedMessage id={`${PAGE_NAME_ID}.columns.${field.labelKey}`} />
                      </HeaderTypography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow sx={{ fontSize: 14 }}>
                  {fields.map((field) => (
                    <TableCell key={field.summaryKey} align="left" sx={{ p: '8px 16px' }}>
                      {renderValue(field, summary)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </React.Fragment>
          ))}
        </NewList>
      </Box>
    </>
  );
};

export default React.memo(ScenarioResultRowBody);
